import palette from "assets/theme/base/palette";
import React, { useState, useEffect } from "react";
import MDButton from "components/MDButton";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import Icon from "@mui/material/Icon";
import Divider from "@mui/material/Divider";
import CircularProgress from "@mui/material/CircularProgress";
import { Dialog } from "@mui/material";
import * as Sentry from "@sentry/browser";
import { call } from "services/axiosService/core";
import { useTranslation } from "react-i18next";
import { useMaterialUIController } from "context";

const CATEGORY_ICONS = {
  "login_reminder":                  "login",
  "assessment_reminder":             "assignment",
  "maxwell_reminder":                "auto_awesome",
  "child/athlete_signup_reminder":   "person_add",
  "child/athlete_assessment_related":"quiz",
  "payment_reminder":                "credit_card",
  "learners_weekly_report":          "bar_chart",
};

const ManageSubscription = ({
  openSubscriptionDialog,
  setOpenSubscriptionDialog,
  setOpenSnackbar,
  setSeverity,
  setAlertMessage,
}) => {
  const [controller] = useMaterialUIController();
  const { persona, profile } = controller;
  const { t } = useTranslation();
  const [categories, setCategories] = useState([]);
  const [preferences, setPreferences] = useState({});
  const [preferencesLoaded, setPreferencesLoaded] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    switch (persona) {
      case "athlete":
        setCategories(["login_reminder", "assessment_reminder", "maxwell_reminder"]);
        break;
      case "guardian":
        setCategories([
          "login_reminder",
          "child/athlete_signup_reminder",
          "child/athlete_assessment_related",
          "payment_reminder",
          "learners_weekly_report",
        ]);
        break;
      case "adultAthlete":
        setCategories([
          "login_reminder",
          "assessment_reminder",
          "maxwell_reminder",
          "child/athlete_signup_reminder",
          "child/athlete_assessment_related",
          "payment_reminder",
          "learners_weekly_report",
        ]);
        break;
      default:
        setCategories(["login_reminder"]);
        break;
    }
  }, []);

  useEffect(() => {
    if (categories.length > 0) getEmailPreferences();
  }, [categories]);

  const getEmailPreferences = () => {
    call(`users/${profile.id}/emailpreferences`, "GET", {})
      .then((response) => {
        if (response.status === 200) {
          const pref = response.data.email_preferences || {};
          categories.forEach((c) => { pref[c] = pref[c] ?? true; });
          setPreferences(pref);
          setPreferencesLoaded(true);
        }
      })
      .catch((error) => { Sentry.captureException(error); });
  };

  const handleToggle = (category) =>
    setPreferences((prev) => ({ ...prev, [category]: !prev[category] }));

  const handleSave = () => {
    setSaving(true);
    call(`users/${profile.id}/emailpreferences`, "PUT", { body: preferences })
      .then((response) => {
        setSaving(false);
        setOpenSnackbar(true);
        if (response.status === 200) {
          setSeverity("success");
          setAlertMessage(t("alerts.profile.preferencesUpdated"));
          setOpenSubscriptionDialog(false);
        } else {
          setSeverity("error");
          setAlertMessage(t("alerts.profile.errorUpdatingPreferences"));
        }
      })
      .catch((error) => {
        Sentry.captureException(error);
        setSaving(false);
        setOpenSnackbar(true);
        setSeverity("error");
        setAlertMessage(t("alerts.profile.errorUpdatingPreferences"));
      });
  };

  const onClose = () => setOpenSubscriptionDialog(false);

  if (!preferencesLoaded) return null;

  return (
    <Dialog
      open={openSubscriptionDialog}
      onClose={onClose}
      fullWidth
      maxWidth="xs"
      PaperProps={{ sx: { borderRadius: "24px", overflow: "hidden" } }}
    >
      {/* Circle close button */}
      <MDBox
        onClick={onClose}
        sx={{
          position: "absolute", top: 12, right: 12, zIndex: 10,
          width: 36, height: 36, borderRadius: "50%",
          bgcolor: palette.neutral[100], border: `1px solid ${palette.neutral[200]}`,
          display: "flex", alignItems: "center", justifyContent: "center",
          cursor: "pointer", transition: "all 180ms ease",
          "&:hover": { bgcolor: palette.neutral[200] },
        }}
      >
        <Icon sx={{ fontSize: "18px !important", color: palette.neutral[600] }}>close</Icon>
      </MDBox>

      <MDBox sx={{ px: 4, pt: 5, pb: 4 }}>
        {/* Header icon + title */}
        <MDBox textAlign="center" mb={3}>
          <MDBox
            sx={{
              width: 80, height: 80, borderRadius: "50%",
              bgcolor: "#9c27b018",
              display: "flex", alignItems: "center", justifyContent: "center",
              mx: "auto", mb: 2,
            }}
          >
            <Icon sx={{ fontSize: "40px !important", color: "#9c27b0" }}>mark_email_read</Icon>
          </MDBox>
          <MDTypography variant="h5" fontWeight="bold" mb={0.75}>
            {t("headings.manageSubscription")}
          </MDTypography>
          <MDTypography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
            {t("messages.profile.manageSubscription")}
          </MDTypography>
        </MDBox>

        {/* Preference toggles */}
        <MDBox
          sx={{
            border: `1px solid ${palette.neutral[200]}`,
            borderRadius: "16px",
            overflow: "hidden",
            mb: 3,
          }}
        >
          {categories.map((category, i) => (
            <MDBox key={category}>
              <MDBox
                display="flex"
                alignItems="center"
                gap={1.5}
                px={2}
                py={1.5}
                sx={{
                  cursor: "pointer",
                  "&:hover": { bgcolor: palette.neutral[50] },
                }}
                onClick={() => handleToggle(category)}
              >
                <MDBox
                  sx={{
                    width: 36, height: 36, borderRadius: "10px",
                    bgcolor: preferences[category] ? `${palette.brand[500]}18` : palette.neutral[100],
                    display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
                    transition: "background 200ms",
                  }}
                >
                  <Icon
                    sx={{
                      fontSize: "18px !important",
                      color: preferences[category] ? palette.brand[500] : palette.neutral[400],
                      transition: "color 200ms",
                    }}
                  >
                    {CATEGORY_ICONS[category] || "notifications"}
                  </Icon>
                </MDBox>

                <MDTypography variant="body2" fontWeight="medium" sx={{ flex: 1, lineHeight: 1.3 }}>
                  {t(`emailPreferences.${category}`)}
                </MDTypography>

                <div
                  onClick={(e) => e.stopPropagation()}
                  style={{
                    display: "inline-flex", borderRadius: "20px", overflow: "hidden",
                    border: "1px solid #ccc", userSelect: "none", width: "72px", height: "28px", flexShrink: 0,
                  }}
                >
                  <span
                    onClick={() => !preferences[category] && handleToggle(category)}
                    style={{
                      flex: 1, display: "flex", alignItems: "center", justifyContent: "center",
                      fontSize: "11px", fontWeight: "bold",
                      backgroundColor: preferences[category] ? palette.brand[500] : palette.neutral[100],
                      color: preferences[category] ? palette.white : palette.neutral[400],
                      cursor: preferences[category] ? "default" : "pointer",
                    }}
                  >
                    ON
                  </span>
                  <span
                    onClick={() => preferences[category] && handleToggle(category)}
                    style={{
                      flex: 1, display: "flex", alignItems: "center", justifyContent: "center",
                      fontSize: "11px", fontWeight: "bold",
                      backgroundColor: preferences[category] ? palette.neutral[100] : palette.brand[500],
                      color: preferences[category] ? palette.neutral[400] : palette.white,
                      cursor: preferences[category] ? "pointer" : "default",
                    }}
                  >
                    OFF
                  </span>
                </div>
              </MDBox>
              {i < categories.length - 1 && <Divider sx={{ m: 0 }} />}
            </MDBox>
          ))}
        </MDBox>

        {/* Buttons */}
        <MDBox display="flex" gap={2}>
          <MDButton
            variant="outlined"
            fullWidth
            onClick={onClose}
            sx={{
              borderRadius: "12px", py: 1.5, textTransform: "none",
              borderColor: palette.neutral[300], color: palette.neutral[600],
              fontWeight: "bold",
              "&:hover": { bgcolor: palette.neutral[100], borderColor: palette.neutral[400] },
            }}
          >
            {t("buttonTitles.cancel") || "Cancel"}
          </MDButton>
          <MDButton
            variant="contained"
            fullWidth
            onClick={handleSave}
            disabled={saving}
            sx={{
              borderRadius: "12px", py: 1.5, textTransform: "none",
              bgcolor: palette.brand[500], color: "#fff", fontWeight: "bold",
              "&:hover": { bgcolor: palette.brand[600] },
              "&.Mui-disabled": { bgcolor: palette.neutral[300], color: "#fff" },
            }}
          >
            {saving
              ? <CircularProgress size={20} color="inherit" />
              : t("buttonTitles.save")
            }
          </MDButton>
        </MDBox>
      </MDBox>
    </Dialog>
  );
};

export default ManageSubscription;
